import { messaging, getToken, onMessage } from '../firebase';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const VAPID_KEY = 'BPwENAP2JdI-YXWcazguACULirrG-nvqCe1XEpGgCn9u96SnYtg3OKfvLo20jTfBjtxonarFB15SIoJFIm217-U';

class NotificationService {
  constructor() {
    this.token = null;
    this.isSupported = messaging !== null;
  }

  /**
   * Check if notifications are supported
   */
  isNotificationSupported() {
    return this.isSupported && 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Request notification permission and register device token
   */
  async requestPermission() {
    if (!this.isNotificationSupported()) {
      console.log('Notifications not supported in this browser');
      return false;
    }

    try {
      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        
        // Register service worker
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service Worker registered:', registration);
        
        // Get FCM token
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration
        });
        
        if (token) {
          console.log('FCM Token:', token);
          this.token = token;
          
          // Register token with backend
          await this.registerTokenWithBackend(token);
          
          // Set up foreground message handler
          this.setupForegroundMessageHandler();
          
          return true;
        } else {
          console.log('No registration token available');
          return false;
        }
      } else {
        console.log('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Register device token with backend
   */
  async registerTokenWithBackend(token) {
    try {
      const authToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!authToken) {
        console.log('No auth token, skipping backend registration');
        return;
      }

      await axios.post(
        `${API_URL}/notifications/register-token`,
        {
          token: token,
          deviceType: 'WEB'
        },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Device token registered with backend');
    } catch (error) {
      console.error('Failed to register token with backend:', error);
    }
  }

  /**
   * Unregister device token (on logout)
   */
  async unregisterToken() {
    if (!this.token) {
      return;
    }

    try {
      const authToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!authToken) {
        return;
      }

      await axios.post(
        `${API_URL}/notifications/unregister-token`,
        {
          token: this.token
        },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Device token unregistered');
      this.token = null;
    } catch (error) {
      console.error('Failed to unregister token:', error);
    }
  }

  /**
   * Setup handler for foreground messages (when app is open)
   */
  setupForegroundMessageHandler() {
    if (!messaging) return;

    onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      
      const { title, body } = payload.notification || {};
      const data = payload.data || {};
      
      // Show browser notification even when app is in foreground
      if (title && Notification.permission === 'granted') {
        const notification = new Notification(title, {
          body: body,
          icon: '/images/KwikLUV.png',
          badge: '/images/KwikLUV.png',
          tag: data.type || 'default',
          data: data
        });

        // Handle notification click
        notification.onclick = () => {
          window.focus();
          notification.close();
          
          // Navigate based on type
          if (data.type === 'MESSAGE' && data.conversationId) {
            window.location.href = `/chat/${data.conversationId}`;
          } else if (data.type === 'CALL') {
            window.location.href = '/inbox';
          } else if (data.type === 'MATCH') {
            window.location.href = '/discover';
          } else if (data.type === 'CREDITS') {
            window.location.href = '/buy-credits';
          } else if (data.type === 'PAYOUT') {
            window.location.href = '/early-payout';
          } else {
            window.location.href = '/inbox';
          }
        };
      }
      
      // You can also show an in-app notification here
      // For example, using a toast library or custom component
    });
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!this.isNotificationSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }
}

export default new NotificationService();
